import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Model } from "survey-core";
import { Survey } from "survey-react-ui";
import { useSearchParams } from "react-router-dom";
import "survey-core/survey-core.min.css";
import "./index.css";

import { listingsSearchCriteriaSurvey } from "./surveys";
import { themeJson } from "./theme";
import { GeoArea, ListingsSearchCriteriaApi, Location, PostCode } from "./services/listingsSearchCriteriaApi";
import { ActionsApi, getOrCreateSessionId } from "./services/actionsApi";
import { asSurveyData } from "./services/surveyUtils";
import ListingsMap, { Neighborhood } from "./ListingsMap";
import PostcodeMap from "./PostcodeMap";
import MapBottomSheet from "./MapBottomSheet";
import MapIconInjector from "./MapIconInjector";
import { PostcodeSelectionState, buildPostcodeIndex, togglePostcode } from "./postcodeUtils";
import { JourneyData } from "./services/tflApi";
import { CommuteResetContext, CommuteResultsContext, JourneyMapContext, NeighbourhoodsContext } from "./SurveyQuestionCommuteFilter";
import "./SurveyQuestionCommuteFilter"; // registers the custom question type
import { API_BASE } from "./config";

const api = new ListingsSearchCriteriaApi(API_BASE);
const actionsApi = new ActionsApi(API_BASE);

// Field names excluded from the generic "FIELD" tracking below.
// matched_properties/postcode_has_selection are computed values we set ourselves — not real user input.
// commute_selected_areas is a hidden field we only ever write to from code (never shown to the user).
// locations and postcode_areas ARE real, directly-typeable fields, but their changes are already
// tracked explicitly as MAP:neighborhood / FORM:neighborhood and MAP:postcodes / FORM:postcodes
// (see handleToggle/handleTogglePostcode and the onValueChanged branches below), so tracking them
// again here would double-count every change.
const INTERNAL_FIELD_NAMES = new Set(["matched_properties", "postcode_has_selection", "postcode_areas", "commute_selected_areas", "locations"]);

function useIsMobile(breakpoint = 768): boolean {
    const [isMobile, setIsMobile] = useState(
        () => window.matchMedia(`(max-width: ${breakpoint - 1}px)`).matches
    );
    useEffect(() => {
        const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
        const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
        mql.addEventListener("change", handler);
        return () => mql.removeEventListener("change", handler);
    }, [breakpoint]);
    return isMobile;
}

export default function ListingsSearchCriteriaSurvey() {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get("userId");

    const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([]);
    const [postcodes, setPostcodes] = useState<PostCode[]>([]);
    const [loading, setLoading] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [locationMode, setLocationMode] = useState<'neighborhood' | 'postcode' | 'commute'>('neighborhood');
    const [postcodeSelection, setPostcodeSelection] = useState<PostcodeSelectionState>({
        selectedCodes: new Set(),
    });
    const [isMapOverlayOpen, setIsMapOverlayOpen] = useState(false);
    const [journeyMap, setJourneyMap] = useState<Map<string, JourneyData>>(new Map());
    const [commuteResetKey, setCommuteResetKey] = useState(0);
    const postcodeTagboxSyncing = useRef(false);
    const commuteAreasSyncing = useRef(false);
    const locationsSyncing = useRef(false);
    const prevLocationMode = useRef(locationMode);
    const lastTrackedFieldValues = useRef<Record<string, string>>({});

    const isMobile = useIsMobile();

    const sessionId = useMemo(() => getOrCreateSessionId(), []);

    const trackAction = useCallback((type: string, actionName: string, properties?: unknown) => {
        actionsApi.sendActions([{
            sessionId,
            userId,
            timestamp: new Date().toISOString(),
            actionName,
            type,
            properties: properties !== undefined ? JSON.stringify(properties) : undefined,
        }]).catch(err => console.error("Failed to send action", err));
    }, [sessionId, userId]);

    const survey = useMemo(() => {
        const s = new Model(listingsSearchCriteriaSurvey);
        s.applyTheme(themeJson);
        return s;
    }, []);

    useEffect(() => {
        Promise.all([
            api.fetchLocations({ includeGeoArea: true }),
            api.fetchPostcodes(),
        ])
            .then(async ([locationsData, postcodesData]: [Location[], PostCode[]]) => {
                const locations = locationsData;
                const q = survey.getQuestionByName("locations");
                if (q) q.choices = locations.map(loc => ({ value: loc.id, text: loc.name }));
                const pq = survey.getQuestionByName("postcode_areas");
                if (pq) pq.choices = postcodesData.map(p => ({ value: p.code, text: p.code }));
                const cq = survey.getQuestionByName("commute_selected_areas");
                if (cq) cq.choices = locations.map(loc => ({ value: loc.id, text: loc.name }));
                setNeighborhoods(
                    locations
                        .filter(loc => !!loc.geoArea)
                        .map(loc => ({ id: loc.id, name: loc.name, geoArea: loc.geoArea as GeoArea }))
                );
                setPostcodes(postcodesData);
                setLoading(false);
            })
            .catch((err: any) => {
                console.error("Failed to load locations", err);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const onComplete = (sender: Model) => {
            setIsCompleted(true);
            trackAction("SURVEY", "complete", sender.data);
            if (!userId) return;
            api.submitSearchCriteria(userId, {
                ...asSurveyData(sender.data),
                postcodes: Array.from(postcodeSelection.selectedCodes),
            })
                .then(data => console.log("Survey results sent to server:", data))
                .catch(err => console.error("Error sending survey data:", err));
        };

        const onValueChanged = (sender: Model, options: { name: string; value: any }) => {
            if (!INTERNAL_FIELD_NAMES.has(options.name)) {
                const serializedValue = JSON.stringify(options.value);
                if (lastTrackedFieldValues.current[options.name] !== serializedValue) {
                    lastTrackedFieldValues.current[options.name] = serializedValue;
                    trackAction("FIELD", options.name, { value: options.value });
                }
            }

            const mode: 'neighborhood' | 'postcode' | 'commute' = sender.data.location_type ?? 'neighborhood';
            setLocationMode(mode);

            if (options.name === 'postcode_areas') {
                if (postcodeTagboxSyncing.current) return;
                const newCodes: string[] = sender.data.postcode_areas || [];
                const newSelected = new Set<string>(newCodes);
                const prevCodes = postcodeSelection.selectedCodes;
                newCodes.filter(code => !prevCodes.has(code))
                    .forEach(code => trackAction("FORM", "postcodes", { code, selected: true }));
                Array.from(prevCodes).filter(code => !newSelected.has(code))
                    .forEach(code => trackAction("FORM", "postcodes", { code, selected: false }));
                survey.setValue("postcode_has_selection", newSelected.size > 0 ? true : undefined);
                setPostcodeSelection({ selectedCodes: newSelected });
                return;
            }

            if (options.name === 'commute_filter') {
                setSelectedIds(options.value || []);
                return;
            }

            if (options.name === 'commute_selected_areas') {
                if (commuteAreasSyncing.current) {
                    if (options.value?.length) {
                        api.matchListings(asSurveyData(sender.data))
                            .then(data => sender.setValue("matched_properties", data.count))
                            .catch(err => console.error("API error", err));
                    } else {
                        sender.setValue('commute_filter', undefined);
                    }
                    return;
                }
                setSelectedIds(options.value || []);
                return;
            }

            // Skip internal fields we set ourselves to avoid recursive calls
            if (['matched_properties', 'postcode_has_selection'].includes(options.name)) return;

            if (mode === 'postcode') {
                const codes = Array.from(postcodeSelection.selectedCodes);
                if (!codes.length) return;
                api.matchListings({ ...asSurveyData(sender.data), postcodes: codes })
                    .then(data => sender.setValue("matched_properties", data.count))
                    .catch(err => console.error("API error", err));
                return;
            }

            if (mode === 'commute') {
                const commuteLocs: string[] | undefined = sender.data.commute_selected_areas;
                if (!commuteLocs?.length) return;
                api.matchListings({ ...asSurveyData(sender.data), locations: commuteLocs })
                    .then(data => sender.setValue("matched_properties", data.count))
                    .catch(err => console.error("API error", err));
                return;
            }

            if (options.name === 'locations' && !locationsSyncing.current) {
                const newIds: string[] = sender.data.locations || [];
                newIds.filter(id => !selectedIds.includes(id))
                    .forEach(id => trackAction("FORM", "neighborhood", { id, selected: true }));
                selectedIds.filter(id => !newIds.includes(id))
                    .forEach(id => trackAction("FORM", "neighborhood", { id, selected: false }));
            }

            setSelectedIds(sender.data.locations || []);
            if (!sender.data.locations?.length) return;
            api.matchListings(asSurveyData(sender.data))
                .then(data => sender.setValue("matched_properties", data.count))
                .catch(err => {
                    console.error("API error", err);
                    sender.setValue("matched_properties", "Error fetching data");
                });
        };

        survey.onComplete.add(onComplete);
        survey.onValueChanged.add(onValueChanged);
        return () => {
            survey.onComplete.remove(onComplete);
            survey.onValueChanged.remove(onValueChanged);
        };
    }, [survey, userId, postcodeSelection, selectedIds, trackAction]);

    // Clear selections when switching modes so nothing bleeds across
    useEffect(() => {
        if (prevLocationMode.current === locationMode) return;
        prevLocationMode.current = locationMode;
        setSelectedIds([]);
        locationsSyncing.current = true;
        survey.setValue('locations', undefined);
        locationsSyncing.current = false;
        survey.setValue("matched_properties", undefined);
        setPostcodeSelection({ selectedCodes: new Set() });
        survey.setValue('postcode_areas', undefined);
        survey.setValue('postcode_has_selection', undefined);
        if (locationMode !== 'commute') {
            setJourneyMap(new Map());
        }
    }, [locationMode, survey]);

    // Sync postcodeSelection.selectedCodes → postcode_areas tagbox
    useEffect(() => {
        if (locationMode !== 'postcode') return;
        postcodeTagboxSyncing.current = true;
        const codes = Array.from(postcodeSelection.selectedCodes);
        survey.setValue("postcode_areas", codes.length ? codes : undefined);
        postcodeTagboxSyncing.current = false;
    }, [survey, postcodeSelection.selectedCodes, locationMode]);

    useEffect(() => {
        const cq = survey.getQuestionByName('commute_selected_areas');
        if (!cq) return;
        cq.visible = false;
        if (locationMode === 'commute') {
            commuteAreasSyncing.current = true;
            cq.value = selectedIds.length ? selectedIds : undefined;
            commuteAreasSyncing.current = false;
        }
    }, [survey, selectedIds, locationMode]);

    useEffect(() => {
        if (locationMode !== 'postcode') return;
        const codes = Array.from(postcodeSelection.selectedCodes);
        if (!codes.length) return;
        api.matchListings({ ...asSurveyData(survey.data), postcodes: codes })
            .then(data => survey.setValue("matched_properties", data.count))
            .catch(err => console.error("API error", err));
    }, [locationMode, postcodeSelection.selectedCodes, survey]);

    const handleModeChange = (mode: 'neighborhood' | 'postcode') => {
        trackAction("CLICK", `mode_change_${mode}`);
        setLocationMode(mode);
        survey.setValue("location_type", mode);
    };

    const handleToggle = (id: string, source: 'map' | 'form') => {
        trackAction(source === 'map' ? "MAP" : "FORM", "neighborhood", { id, selected: !selectedIds.includes(id) });
        const next = selectedIds.includes(id)
            ? selectedIds.filter(v => v !== id)
            : [...selectedIds, id];
        setSelectedIds(next);
        locationsSyncing.current = true;
        survey.setValue("locations", next.length ? next : undefined);
        locationsSyncing.current = false;
    };

    const postcodeIndex = useMemo(
        () => buildPostcodeIndex(postcodes),
        [postcodes],
    );

    const handleClearAllCommute = () => {
        trackAction("CLICK", "clear_all_commute");
        setSelectedIds([]);
        setJourneyMap(new Map());
        setCommuteResetKey(k => k + 1);
    };

    const handleCommuteAreaToggle = (id: string, source: 'map' | 'form') => {
        trackAction(source === 'map' ? "MAP" : "FORM", "neighborhood", { id, selected: !selectedIds.includes(id) });
        const next = selectedIds.includes(id)
            ? selectedIds.filter(v => v !== id)
            : [...selectedIds, id];
        setSelectedIds(next);
        if (selectedIds.includes(id)) {
            setJourneyMap(prev => {
                const m = new Map(prev);
                m.delete(id);
                return m;
            });
        }
        // commute_selected_areas is synced by the useEffect below
    };

    const handleTogglePostcode = (code: string) => {
        trackAction("MAP", "postcodes", { code, selected: !postcodeSelection.selectedCodes.has(code) });
        setPostcodeSelection(prev => {
            const next = togglePostcode(code, postcodeIndex, prev);
            const hasSelection = next.selectedCodes.size > 0;
            survey.setValue("postcode_has_selection", hasSelection ? true : undefined);
            return next;
        });
    };

    if (loading) return <p>Loading survey...</p>;
    if (!userId) return <p>Error: User ID is required to access this survey.</p>;

    if (isCompleted) {
        return (
            <div style={{ height: "100vh", background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Survey model={survey} />
            </div>
        );
    }

    const surveyEl = (
        <CommuteResetContext.Provider value={commuteResetKey}>
            <JourneyMapContext.Provider value={setJourneyMap}>
                <NeighbourhoodsContext.Provider value={neighborhoods}>
                    <CommuteResultsContext.Provider value={{
                        ids: locationMode === 'commute' ? selectedIds : [],
                        onRemove: (id: string) => handleCommuteAreaToggle(id, 'form'),
                        onClearAll: handleClearAllCommute,
                        onOpenMap: () => {
                            trackAction("CLICK", "open_map_commute");
                            setIsMapOverlayOpen(true);
                        },
                    }}>
                        <Survey model={survey} />
                    </CommuteResultsContext.Provider>
                </NeighbourhoodsContext.Provider>
            </JourneyMapContext.Provider>
        </CommuteResetContext.Provider>
    );

    // Desktop
    if (!isMobile) {
        return (
            <div style={{ display: "grid", gridTemplateColumns: "420px 1fr", height: "100vh" }}>
                <div style={{ overflow: "auto" }}>
                    {surveyEl}
                </div>
                <div style={{ position: "relative", height: "100%" }}>
                    {locationMode === 'postcode'
                        ? <PostcodeMap postcodes={postcodes} selectionState={postcodeSelection} onToggle={handleTogglePostcode} />
                        : <ListingsMap
                            neighborhoods={neighborhoods}
                            selectedIds={selectedIds}
                            onToggle={locationMode === 'neighborhood' ? (id: string) => handleToggle(id, 'map') : undefined}
                            journeyMap={locationMode === 'commute' ? journeyMap : undefined}
                          />
                    }
                </div>
            </div>
        );
    }

    // Mobile: survey with map overlay
    return (
        <div>
            <div style={{ background: "#f0f2f5" }}>
                {surveyEl}
            </div>
            {locationMode === 'neighborhood' && (
                <MapIconInjector onOpen={() => {
                    trackAction("CLICK", "open_map_locations");
                    setIsMapOverlayOpen(true);
                }} />
            )}
            {locationMode === 'postcode' && (
                <MapIconInjector
                    questionName="postcode_areas"
                    buttonId="postcode-map-icon-btn"
                    onOpen={() => {
                        trackAction("CLICK", "open_map_postcode_areas");
                        setIsMapOverlayOpen(true);
                    }}
                />
            )}
            {isMapOverlayOpen && (
                <MapBottomSheet
                    neighborhoods={neighborhoods}
                    selectedIds={selectedIds}
                    onToggle={locationMode === 'commute' ? handleCommuteAreaToggle : handleToggle}
                    onClose={() => setIsMapOverlayOpen(false)}
                    locationMode={locationMode === 'commute' ? 'commute' : locationMode}
                    onModeChange={handleModeChange}
                    postcodes={postcodes}
                    postcodeSelection={postcodeSelection}
                    onTogglePostcode={handleTogglePostcode}
                    journeyMap={locationMode === 'commute' ? journeyMap : undefined}
                />
            )}
        </div>
    );
}
